/* Shared Activate utilities — Live API paths, seat posture, operator errors.
 * Honesty: MAC alone cannot identify
 */

import { path } from "./api.js";

export const STEPS = ["Identify", "Verify", "Claim", "Interfaces", "Capabilities", "Position", "Done"];
export const KEYS = ["identify", "verify", "claim", "interfaces", "capabilities", "position", "done"];
export const CLAIMABLE = new Set(["ready", "ready_factory"]);

/** True when Live identify status + MAC allow POST /provision/claim. */
export function claimableIdentifyOf(identify) {
  const mac = String((identify && identify.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "");
  return !!(identify && CLAIMABLE.has(identify.status) && mac.length === 12);
}

/** Best machine href id from claim output, typed id, or already_registered identify. */
export function activateMachineIdOf(identify, claimOut, deviceId) {
  return String(
    (claimOut && claimOut.device_id)
    || deviceId
    || (identify && identify.status === "already_registered" && identify.device_id)
    || ""
  ).trim();
}

export function identifyStatusTone(status) {
  const st = String(status || "");
  if (CLAIMABLE.has(st)) return "go";
  if (st === "already_registered" || st === "offline") return "warn";
  return "bad";
}

/** Live claim device_id — mirrors config.DEVICE_ID_REGEX. */
export const DEVICE_ID_RE = /^(SN-[A-Z2-7]{16}|CROC-[0-9A-F]{12})$/i;
export const FACTORY_SN_RE = /^SN-[A-Z2-7]{16}$/i;

/** ACTIVATE-UX: max reachable step index from Live session state (fail-closed). */
export function activateMaxStepOf({ identify, claimOut, deviceId }) {
  const claimed = !!(deviceId || (claimOut && (claimOut.device_id || claimOut.ok || claimOut.status === "claimed")));
  if (claimed) return 6;
  if (identify && (identify.ok || identify.status || identify.mac || identify.serial || identify.device_id)) {
    return 2;
  }
  return 0;
}

export function activateMaxPhaseOf({ identify, claimOut, deviceId }) {
  const claimed = !!(deviceId || (claimOut && (claimOut.device_id || claimOut.ok || claimOut.status === "claimed")));
  if (claimed) return 2;
  const mac = String((identify && identify.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "");
  if (identify && CLAIMABLE.has(identify.status) && mac.length === 12) return 1;
  if (identify && (identify.status || identify.serial)) return 0;
  return 0;
}

export function safeActivatePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__activate_status") return "/activate/status";
    if (op === "get__provision_pending") return "/provision/pending";
    if (op === "post__provision_identify") return "/provision/identify";
    if (op === "post__provision_claim") return "/provision/claim";
    if (op === "post__provision_challenge_request") return "/provision/challenge/request";
    if (op === "post__provision_challenge_verify") return "/provision/challenge/verify";
    return "";
  }
}

export function softActivateErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/activate_user_floor|user_floor/.test(low)) {
    return "Your account cannot activate devices. Ask a manager if you need access.";
  }
  if (/activate_staff_device_admin_required|staff_device_admin/.test(low)) {
    return "Staff need device-admin capability to activate. Ask Platform.";
  }
  if (/activate_.*_sealed|seat_gated|activate_all_sealed/.test(low)) {
    return "Activate seats are closed for this account right now.";
  }
  if (/activate_capability_denied|forbidden/.test(low) && /\b403\b|denied/.test(low)) {
    return "Your account cannot activate devices. Ask a manager if you need access.";
  }
  if (/\b403\b|forbidden|insufficient|not_authorized|operation denied|user_floor|seat_gated/.test(low)) {
    return "Your account cannot activate devices. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That unit or queue item is not available.";
  }
  if (/\b409\b|already|conflict|busy/.test(low)) {
    return "That unit is already in play elsewhere.";
  }
  if (/\b410\b|expired/.test(low)) {
    return "That challenge expired — request a new one.";
  }
  if (/\b412\b|challenge required|verified device challenge/.test(low)) {
    return "This site requires a verified challenge before claim. Open Legacy challenge, or turn enforce off.";
  }
  if (/provision_tasks_store|wifi-task|wifi_task/.test(low) && /\b503\b|unavailable/.test(low)) {
    return "Wi‑Fi task store is unavailable right now.";
  }
  if (/\b400\b|invalid|required|mac|serial|device_id|qr_code/.test(low)) {
    return "That identify or claim was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|disabled|offline/.test(low)) {
    return "Activate is not open right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/provision\/|\/devices\/|\/activate\/|http|GET |POST |PATCH |status_path/.test(low)) {
    return "That action could not be completed.";
  }
  return "That action could not be completed.";
}

export function activateSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paint = Number(lim.pending_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    queueAllowed: p.queue_plane_allowed === true,
    custodyAllowed: p.custody_plane_allowed === true,
    identifyAllowed: p.identify_allowed === true,
    pendingAllowed: p.pending_allowed === true,
    claimAllowed: p.claim_allowed === true,
    canClaimDevice: p.can_claim_device !== false,
    challengeAllowed: p.challenge_allowed === true,
    wireAllowed: p.wire_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    pendingPaintMax:
      Number.isFinite(paint) && paint > 0 ? Math.floor(paint) : 48,
  };
}

export function fmtMac(m) {
  const s = String(m || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  if (s.length !== 12) return s || "—";
  return s.match(/.{2}/g).join(":");
}

export function hwIdFromMac(m) {
  const s = String(m || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  return s.length === 12 ? ("CROC-" + s) : "";
}

/** Live claim device_id: SN-… or CROC-<MAC12>. Prefer identify.serial, then typed, then CROC-MAC. */
export function liveClaimDeviceIdOf(identify, typed) {
  const candidates = [
    String(typed || "").trim().toUpperCase(),
    String((identify && identify.serial) || "").trim().toUpperCase(),
    String((identify && identify.device_id) || "").trim().toUpperCase(),
    hwIdFromMac((identify && identify.mac_nocolon) || ""),
  ];
  for (const c of candidates) {
    if (c && DEVICE_ID_RE.test(c)) return c;
  }
  return "";
}

/** Pending row → Live identify body, or error if Soft MAC-only. */
export function pendingIdentifyBodyOf(row) {
  const r = row && typeof row === "object" ? row : {};
  const qr = String(r.qr_code || "").trim();
  const proposed = String(r.proposed_device_id || "").trim();
  if (qr.includes("|") || qr.toUpperCase().startsWith("CROC|")) return { body: { qr_code: qr } };
  if (FACTORY_SN_RE.test(proposed)) return { body: { serial: proposed.toUpperCase() } };
  return {
    error: "This waiting row has no sticker serial. Type the serial above — the short network code alone will not work.",
  };
}

export function hintsOf(res) {
  const h = res && res.operator_hints;
  if (!h) return [];
  if (Array.isArray(h)) return h;
  return h.en || h.zh || [];
}

export function normalizeQrPayload(raw) {
  return String(raw || "").trim();
}
